<template>
  <div class="audio-player-wrapper" :class="$attrs.class">
    <div class="audio-container">
      <div v-if="showStartButton" class="audio-start-button">
        <el-button
          type="primary"
          size="small"
          @click="startFromTime"
          :loading="loading"
          class="start-button"
        >
          播放
        </el-button>
      </div>
      <audio
        ref="audioRef"
        class="audio-player"
        :src="audioSrc"
        controls="controls"
        v-bind="otherAttrs"
      ></audio>
      <a :href="src" type="text" size="small" target="_blank" class="download-button"> 下载 </a>
    </div>
  </div>
</template>

<script>
export default {
  name: "AudioPlayer",
  inheritAttrs: false,
  props: {
    // 音频源地址
    src: {
      type: String,
      required: true
    },
    // 从第几秒开始播放
    startTime: {
      type: Number,
      default: 3
    },
    // 是否显示控制条
    controls: {
      type: [Boolean, String],
      default: "controls"
    },
    // 是否显示开始播放按钮
    showStartButton: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      audioSrc: "",
      blobUrl: null,
      loading: false
    };
  },
  computed: {
    otherAttrs() {
      // eslint-disable-next-line no-unused-vars
      const { class: _, controls: __, ...attrs } = this.$attrs;
      return attrs;
    }
  },
  watch: {
    src: {
      immediate: true,
      handler(newSrc) {
        if (newSrc) {
          this.loadAudioAsBlob(newSrc);
        }
      }
    }
  },
  beforeDestroy() {
    // 清理 Blob URL
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  },
  methods: {
    async loadAudioAsBlob(src) {
      try {
        this.loading = true;
        const response = await fetch(src);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        if (this.blobUrl) {
          URL.revokeObjectURL(this.blobUrl);
        }

        this.blobUrl = blobUrl;
        this.audioSrc = blobUrl;
      } catch (error) {
        // 如果失败，使用原始 URL
        this.audioSrc = src;
      } finally {
        this.loading = false;
      }
    },
    startFromTime() {
      const audio = this.$refs.audioRef;
      if (!audio) return;

      const targetTime = this.startTime;

      if (audio.readyState < 1) {
        audio.load();
        const onLoadedMetadata = () => {
          this.setTimeAndPlay(audio, targetTime);
        };
        audio.addEventListener("loadedmetadata", onLoadedMetadata, { once: true });
      } else {
        this.setTimeAndPlay(audio, targetTime);
      }
    },
    setTimeAndPlay(audio, targetTime) {
      if (!audio.duration || !isFinite(audio.duration)) {
        audio.play().catch(() => {
          // 静默处理播放失败
        });
        return;
      }

      const safeTime = Math.min(targetTime, audio.duration - 0.1);
      audio.pause();
      audio.currentTime = safeTime;

      setTimeout(() => {
        const actualTime = audio.currentTime;
        const diff = Math.abs(actualTime - safeTime);

        if (diff < 0.5) {
          audio.play().catch(() => {
            // 静默处理播放失败
          });
        } else {
          this.tryRangeRequest(audio, safeTime);
        }
      }, 200);
    },
    tryRangeRequest(audio, targetTime) {
      if (this.$message) {
        this.$message({
          message: `该音频不支持跳转到第${targetTime}秒，将从开头播放。请使用播放器的进度条手动跳转。`,
          type: "warning",
          duration: 4000
        });
      }
      audio.play().catch(() => {
        // 静默处理播放失败
      });
    }
  }
};
</script>

<style scoped>
.audio-player-wrapper {
  display: flex;
  flex-direction: column;
}

.audio-container {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.audio-start-button {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.audio-start-button .start-button {
  height: 26px;
  font-size: 12px;
  padding: 0 12px;
  white-space: nowrap;
}

.audio-player {
  display: block;
  flex: 1;
  height: 32px;
  outline: none;
  min-width: 0;
}

.audio-player::-webkit-media-controls-panel {
  background-color: #f5f5f5;
}

.download-button {
  font-size: 12px;
  color: #409eff;
  cursor: pointer;
}
</style>
